'use client';

import React, { useState, useEffect } from 'react';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Stall {
  id: string;
  name: string;
  description: string;
  photo_url: string;
  visible: boolean;
}

export default function StallHolders() {
  const [stalls, setStalls] = useState<Stall[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    const fetchStalls = async () => {
      try {
        const { data, error } = await supabase
          .from('stalls')
          .select('*')
          .eq('visible', true)
          .order('sort_order', { ascending: true });
        if (!error && data) setStalls(data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchStalls();

    // Real-time subscription for stalls table
    const channel = supabase
      .channel('realtime:stalls')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'stalls' },
        async () => {
          // Re-fetch full visible sorted list on any change
          const { data, error } = await supabase
            .from('stalls')
            .select('*')
            .eq('visible', true)
            .order('sort_order', { ascending: true });
          if (!error && data) setStalls(data);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Stall Holders</h2>
      <p className="text-sm text-muted-foreground">
        Browse our local stall holders and traders at today's event.
      </p>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-2xl border border-border h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {stalls?.map((stall) => (
            <div
              key={stall?.id}
              className="rounded-2xl overflow-hidden card-shadow border border-border bg-card text-center transition-all duration-200 active:scale-95"
            >
              {stall?.photo_url ? (
                <div className="relative w-full h-24">
                  <Image
                    src={stall.photo_url}
                    alt={stall.name}
                    fill
                    className="object-cover"
                  />
                </div>
              ) : (
                <div className="w-full h-24 bg-muted flex items-center justify-center">
                  <ShoppingBagIcon className="w-8 h-8 text-muted-foreground/40" />
                </div>
              )}
              <div className="p-3">
                <p className="text-xs font-bold leading-tight mb-1 text-foreground">{stall?.name}</p>
                {stall?.description && (
                  <p className="text-[10px] text-muted-foreground">{stall.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-muted rounded-2xl p-4 text-center border border-border">
        <ShoppingBagIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-semibold text-foreground mb-1">Interested in a stall?</p>
        <p className="text-xs text-muted-foreground mb-3">
          Contact Lisa on 07801 658906 or email venues@newmiltonrfc.co.uk
        </p>
        <a
          href="tel:07801658906"
          className="inline-flex items-center gap-2 gradient-primary text-secondary text-xs font-bold px-4 py-2.5 rounded-xl transition-all duration-150 active:scale-95"
        >
          📞 Call to Book
        </a>
      </div>
    </div>
  );
}